import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import plural from 'plural-ru'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import FeaturesList from './FeaturesList'
import Gift from './Gift'
import Button from './Button'
import Spinner from './Spinner'
import Promocode from '../components/Promocode'
import { MountContext } from './Mount'
import Alert from './Alert'
import { activeWebTrialOfferPricesSelector, profileSelector } from '../reducers/profile'

import products_new, { POSSIBLE_SUBSCRIPTION_TYPES, getWidgetData } from '../constants/ProductsNew'
import { getAccount } from '../actions/api'
import { getDiscountsSelector } from '../reducers/discounts'
import { setDiscounts } from '../actions/utils'
import scrollTo from '../utils/scrollTo'
import scrollToElement from '../utils/scrollToElement'
import find from 'lodash/find'
import get from 'lodash/get'
import difference from 'lodash/difference'
import SubscriptionSelectNew from './SubscriptionSelectNew'

import '../styles/subscription.scss'

const CALLBACK_DEEPLINK_TEMPLATES = {
  radio: "https://deed3.app.goo.gl/?link={{payload_link}}&apn=com.azradio&ibi=com.arzamas.radio&ius=arzamasradio&isi=1261860228",
  goosegoose: "https://goosegoosegg.page.link/?link={{payload_link}}&apn=gg.goosegoose&ibi=gg.goosegoose&isi=1534779649"
}

const SubscriptionPurchaseOptions = ({
  discounts,
  setDiscounts,
  getAccount,
  profile,
  activeWebTrialOfferPrices,
  activeAppleSubscriptionTypes,
  activeGoogleSubscriptionTypes,
  activeWebSubscriptionTypes,
  subscriptionTypes
}) => {
  const location = useLocation()
  const query = useMemo(() => queryString.parse(location.search), [location.search])
  const mount = useContext(MountContext)
  const [loading, setLoading] = useState(false)
  const yourSubscriptionEl = useRef(null)
  const newSubscriptionEl = useRef(null)
  const availableTypes = difference(
    POSSIBLE_SUBSCRIPTION_TYPES.map(item => item.type),
    activeWebSubscriptionTypes
  )

  let allowedQueryProduct
  let allowedQueryProductType
  if (query?.product) {
    const queryProductType = products_new.find(item => item.product === query.product)?.type
    if (queryProductType && availableTypes.includes(queryProductType)) {
      allowedQueryProduct = query.product
      allowedQueryProductType = queryProductType
    }
  }

  const [selectedType, setSelectedType] = useState(allowedQueryProductType || availableTypes[availableTypes.length - 1])
  const [extraTipCents, setExtraTipCents] = useState(0)

  const defaultSelectedProduct = allowedQueryProduct || (selectedType
    ? products_new.find(item => item.type === selectedType && item.product.includes('yearly'))['product']
    : null)

  const [selectedProduct, setSelectedProduct] = useState(defaultSelectedProduct)

  const isLifetime = selectedProduct && selectedProduct.includes('lifetime')

  const paymentEvent = new CustomEvent('payment')
  const selectedProductData = find(products_new, { product: selectedProduct })
  const fullPrice = get(selectedProductData, 'price')
  let discountType = get(selectedProductData, 'discountType')
  let camelCaseId = get(selectedProductData, 'camelCaseId')

  let discountPrice = null
  if (discounts[discountType]) {
    discountPrice = fullPrice - (discounts[discountType] || 0) / 100
  }
  const plannedSubscriptionPrice = fullPrice + extraTipCents / 100

  // эта инфа приходит с бекенда и фронтенд меняет наименования на camel case, приходится искать по нему
  const trialPriceForSelectedProduct = discountPrice ? null : activeWebTrialOfferPrices[camelCaseId]

  // Совпадает ли начальная цена и регулярная (цена ежемесячного списания)
  const priceMatches = Number(getPriceWithOutExtraTip()) === fullPrice

  useEffect(() => {
    if (allowedQueryProduct) {
      handlePayClick()
    } else if (query?.product && query?.app_flavor) {
      redirectBackToApp({status: 'failed', code: 'already_purchased', purchase_details_payload: query.purchase_details_payload})
    }
  }, [])

  return (
    <div className="account-block">
      <div className="account-caption">{subscriptionTypes.length ? 'Новая подписка:' : 'Выберите подписку:'}</div>
      <div className="account-wrapper account-wrapper--management account-wrapper--management-new">
        <SubscriptionSelectNew
          activeProduct={selectedProduct}
          onProductChange={handleProductChange}
          excludedTypes={activeWebSubscriptionTypes}
        />
        <Promocode
          subscriptions={profile.subscriptions}
          activeSubscriptionTypes={profile.activeSubscriptionTypes}
          yourSubscriptionEl={yourSubscriptionEl}
          newSubscriptionEl={newSubscriptionEl}
          extraTipCents={extraTipCents}
          setExtraTipCents={setExtraTipCents}
        />
        <Button onClick={handlePayClick} disabled={loading} isSubscriptionInPromo>
          {loading ? <Spinner /> : getButtonText()}
        </Button>

        {getMessage() && <div className="account-subscription__message">{getMessage()}</div>}
        <FeaturesList />
      </div>

      <div className="account-wrapper">
        <Gift />
      </div>
    </div>
  )

  function redirectBackToApp(params) {
    console.log('redirectBackToApp', params)
    const payloadLinkParams = queryString.stringify(params)
    const payloadLink = "https://arzamas.academy/account?" + payloadLinkParams
    console.log('redirectBackToApp payloadLink', payloadLink)
    if (!CALLBACK_DEEPLINK_TEMPLATES[query.app_flavor]) {
      console.error('redirectBackToApp: unknown app_flavor', query.app_flavor)
      return
    }
    const redirectTo = CALLBACK_DEEPLINK_TEMPLATES[query.app_flavor].replace('{{payload_link}}', encodeURIComponent(payloadLink))
    console.log('Will redirect to', redirectTo)
    window.location.href = redirectTo
  }

  async function handlePayClick() {
    const transactionId = get(profile, 'uuid') + '/' + selectedProduct + '/' + Math.floor(Date.now() / 1000)
    let widgetData = getWidgetData(selectedProduct, discounts, activeWebTrialOfferPrices, extraTipCents)

    if (window.SubscriptionAnalytics) {
      window.SubscriptionAnalytics.trackSubscriptionPurchaseClick(
        selectedProduct,
        widgetData.amount,
        widgetData.data.discount_code,
        transactionId,
        extraTipCents
      )
    }
    widgetData.accountId = get(profile, 'uuid')
    if (get(profile, 'email')) {
      widgetData.email = get(profile, 'email')
      if (get(widgetData, 'data.cloudPayments.customerReceipt.email')) {
        widgetData.data.cloudPayments.customerReceipt.email = get(profile, 'email')
      }
    }
    widgetData.requireEmail = true
    console.log('widgetData', widgetData)
    try {
      const widget = new window.cp.CloudPayments()
      if (window.SubscriptionAnalytics) {
        window.SubscriptionAnalytics.trackSubscriptionPurchaseWidgetOpened(
          selectedProduct,
          widgetData.amount,
          widgetData.data.discount_code,
          transactionId
        )
      }
      widget.charge(
        widgetData,
        async () => {
          if (window.SubscriptionAnalytics) {
            window.SubscriptionAnalytics.trackSubscriptionPurchase(
              selectedProduct,
              widgetData.amount,
              widgetData.data.discount_code,
              transactionId,
              extraTipCents
            )
          }
          setLoading(true)
          setDiscounts({})
          await getAccount()
          setLoading(false)

          if (query?.platform === 'android' || query?.platform === 'ios') {
            redirectBackToApp({status: 'success', purchase_details_payload: query.purchase_details_payload})
            return
          }

          if (selectedType !== 'pack') {
            document.dispatchEvent(paymentEvent)
          }

          // отскролл к подписке
          setTimeout(() => {
            scrollTo(window.offsetTop, 500)
          }, 300)

          const hasSimilarSubscriptionInAppStore =
            activeAppleSubscriptionTypes.includes(selectedType) ||
            (selectedType === 'pack' && activeAppleSubscriptionTypes.length)
          const hasSimilarSubscriptionInGooglePlay =
            activeGoogleSubscriptionTypes.includes(selectedType) ||
            (selectedType === 'pack' && activeGoogleSubscriptionTypes.length)
          const hasRadioSubscription = selectedType === 'pack' && subscriptionTypes.includes('radio')
          const hasKidsSubscription = selectedType === 'pack' && subscriptionTypes.includes('kids')

          // Показ модалки если подписка куплена в сторе
          if (hasSimilarSubscriptionInAppStore || hasSimilarSubscriptionInGooglePlay) {
            const elementId = activeAppleSubscriptionTypes
              ? activeAppleSubscriptionTypes[0]
              : activeGoogleSubscriptionTypes[0]
            mount.push(Alert, {
              props: {
                title: 'Внимание!',
                message: `Спасибо, что купили общую подписку! Чтобы не&nbsp;платить дважды, не&nbsp;забудьте отменить автоматическое продление своей старой подписки&nbsp;в ${
                  hasSimilarSubscriptionInAppStore ? 'App Store' : 'Google Play'
                }.`,
                buttons: [
                  {
                    text: 'Управление',
                    onClick: () => {
                      mount.pop()
                      scrollToElement(elementId)
                    }
                  }
                ]
              }
            })
          }

          // Показ модалки если есть купленные подписки при покупке общей
          if ((hasRadioSubscription || hasKidsSubscription) && profile.subscriptions[0].kind === 'web_subscription') {
            const elementId = hasRadioSubscription ? 'radio' : 'kids'
            mount.push(Alert, {
              props: {
                title: 'Внимание!',
                message: `Спасибо, что купили общую подписку! Чтобы не&nbsp;платить дважды, не&nbsp;забудьте отменить автоматическое продление своей старой подписки&nbsp;на ${
                  hasRadioSubscription ? '&laquo;Радио Arzamas&raquo;' : '&laquo;Гусьгусь&raquo;'
                }${hasRadioSubscription && hasKidsSubscription && ' и &laquo;Гусьгусь&raquo;'}.`,
                buttons: [
                  {
                    text: 'Управление',
                    onClick: () => {
                      mount.pop()
                      scrollToElement(elementId)
                    }
                  }
                ]
              }
            })
          }
        },
        (reason, options) => {
          console.log('CloudPayments fail:', reason, options)
          if (window.SubscriptionAnalytics) {
            window.SubscriptionAnalytics.trackSubscriptionPurchaseFailed(selectedProduct, transactionId)
          }
          if (query?.platform === 'android' || query?.platform === 'ios') {
            // redirect to app
            const cancelledByUser = reason === "User has cancelled"
            let payloadParams = {
              status: 'failed',
              code: cancelledByUser ? 'user_cancelled' : 'payment_failed',
              purchase_details_payload: query.purchase_details_payload
            }
            if (!cancelledByUser) {
              payloadParams['message'] = reason
            }
            redirectBackToApp(payloadParams)
          }
        },
        (paymentResult, options) => {
          console.log('CloudPayments complete:', paymentResult, options)
        }
      )
    } catch (error) {
      console.log('CloudPayments error:', error)
    }
  }

  function handleProductChange(selectedType, selectedProduct) {
    setSelectedType(selectedType)
    setSelectedProduct(selectedProduct)
  }

  function getMessage() {
    // если начальная цена не равна регулярной
    if (!priceMatches && !isLifetime) {
      // Если есть триал
      if (trialPriceForSelectedProduct) {
        // Если есть добровольная часть
        if (extraTipCents) {
          return `Внимание! Следующее списание будет по цене ${plannedSubscriptionPrice} ₽. После пробного периода в силу вступит выбранный
          вами план подписки. Если у вас есть действующая подписка, промокод заработает после ее окончания.`
        } else {
          return `Внимание! Следующее списание будет по стандарной цене ${plannedSubscriptionPrice} ₽. После пробного периода в силу вступит выбранный
          вами план подписки. Если у вас есть действующая подписка, промокод заработает после ее окончания.`
        }
      } else {
        // Если есть добровольная часть
        if (extraTipCents) {
          return `Внимание! Следующее списание будет по цене ${plannedSubscriptionPrice} ₽.`
        } else {
          return `Внимание! Следующее списание будет по стандартной цене ${plannedSubscriptionPrice}`
        }
      }
    }

    return false
  }

  function getButtonText() {
    if (priceMatches) {
      return `Купить за ${getPrice()} ${plural(getPrice(), 'рубль', 'рубля', 'рублей')} ${geyType(selectedProduct)}`
    } else {
      // не указываем период, если начальная цена не равна регулярной
      return `Купить за ${getPrice()} ${plural(getPrice(), 'рубль', 'рубля', 'рублей')}`
    }
  }

  function getPriceWithOutExtraTip() {
    return trialPriceForSelectedProduct || discountPrice || fullPrice
  }

  function getPrice() {
    return getPriceWithOutExtraTip() + extraTipCents / 100
  }

  function geyType(activeProduct) {
    let type = ''
    const productType = {
      monthly: 'в месяц',
      yearly: 'в год',
      triennial: 'за 3 года',
      lifetime: 'навсегда'
    }
    if (activeProduct?.includes('monthly')) {
      type = productType['monthly']
    } else if (activeProduct?.includes('triennial')) {
      type = productType['triennial']
    } else if (activeProduct?.includes('yearly')) {
      type = productType['yearly']
    } else if (activeProduct?.includes('lifetime')) {
      type = productType['lifetime']
    }

    return type
  }
}

const mapStateToProps = createStructuredSelector({
  profile: profileSelector,
  activeWebTrialOfferPrices: activeWebTrialOfferPricesSelector,
  discounts: getDiscountsSelector
})

export default connect(mapStateToProps, { setDiscounts, getAccount })(SubscriptionPurchaseOptions)
